import Button from '@/app/components/common/Button'
import CounterInput from '@/app/components/common/CounterInput'
import { Icons } from '@/app/components/ui/Icons'
import { Allocation } from '@/app/types'
import { isRtlLanguage } from '@/app/utils'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { CustomScroll } from 'react-custom-scroll'
import Select, {
	components,
	MenuListProps,
	SingleValue,
	SingleValueProps,
} from 'react-select'
import {
	emitAddRoomClickGAEvent,
	emitSaveGuestsClickGAEvent,
} from '../ga-event-helpers'

type OptionType = {
	value: number
	label: string
}

type BreakpointVariant = 'lg' | 'md'

interface GuestsSelectProps {
	initialValue: Allocation[]
	isMobile: boolean
	onChange: (value: Allocation[]) => void
	onClose: () => void
	onSave?: (value: Allocation[]) => void
	onApplyGuestsAndSearch?: () => void
	isMainPage?: boolean
	breakpointVariant?: BreakpointVariant
}

const MAX_ADULTS_PER_ROOM = 4
const MAX_CHILDREN_PER_ROOM = 4
const MAX_ROOMS = 8
const MIN_ADULTS = 1
const MAX_COUNT_PER_ROOM = 4

const GuestsSelect: FC<GuestsSelectProps> = ({
	onChange,
	onClose,
	onSave,
	isMobile,
	isMainPage = false,
	onApplyGuestsAndSearch,
	initialValue,
	breakpointVariant = 'lg',
}) => {
	const lng = usePathname().split('/')[1] || 'en'
	const isRtl = isRtlLanguage(lng)
	const scrollRef = useRef<HTMLDivElement>(null)
	const childAgesContainerRefs = useRef<Record<number, HTMLDivElement | null>>(
		{}
	)
	const t = useTranslations('Form')
	const [rooms, setRooms] = useState<Allocation[]>(initialValue)

	// Helper functions to generate responsive classes based on breakpoint
	// This is needed because in some instances on tablets we use desktop view with a Popup
	// and in other instances we use mobile view (most of the time)
	// If LG breakpoint variant -> will use mobile view from 1024 down
	// if MD variant -> will use mobile view from 768px down
	const getDesktopClass = (className: string) =>
		`${breakpointVariant}:${className}`
	const getMobileClass = (className: string) =>
		`max-${breakpointVariant}:${className}`
	const getHiddenDesktopClass = () => `hidden ${breakpointVariant}:flex`
	const getHiddenMobileClass = () => `${breakpointVariant}:hidden`
	const getDesktopBlockClass = () => `hidden ${breakpointVariant}:block`
	const [disclaimersArray, setDisclaimerArray] = useState<string[]>([])
	const shouldViewMobileLayout = breakpointVariant === 'lg'

	useEffect(() => {
		onChange(rooms)
	}, [rooms, onChange])

	useEffect(() => {
		// This useEffect is used to manage disclaimers based on the current state of rooms
		const isMaxGuests = rooms.some(
			(room) => room.adults + room.childAge.length === MAX_COUNT_PER_ROOM
		)
		const isMaxRooms = rooms.length >= MAX_ROOMS
		const hasMaxGuestsDisclaimer = disclaimersArray.includes('maxGuests')
		const hasMaxRoomsDisclaimer = disclaimersArray.includes('maxRooms')
		let result = [...disclaimersArray]
		if (isMaxGuests && !hasMaxGuestsDisclaimer) {
			result.unshift('maxGuests')
		} else if (!isMaxGuests && hasMaxGuestsDisclaimer) {
			result = result.filter((item) => item !== 'maxGuests')
		}
		if (isMaxRooms && !hasMaxRoomsDisclaimer) {
			result.unshift('maxRooms')
		} else if (!isMaxRooms && hasMaxRoomsDisclaimer) {
			result = result.filter((item) => item !== 'maxRooms')
		}
		if (result.length !== disclaimersArray.length) {
			result = result.slice(0, 2)
			setDisclaimerArray(result)
		}
	}, [rooms, disclaimersArray])

	const isCloseToBottom = useCallback((roomIndex: number) => {
		const container = childAgesContainerRefs.current[roomIndex]
		if (!container) return false

		const rect = container.getBoundingClientRect()
		const windowHeight = window.innerHeight
		const spaceBelow = windowHeight - rect.bottom

		return spaceBelow < 250
	}, [])

	const NUMBER_OPTIONS_CHILDREN = new Array(18).fill(0).map((_, i) => ({
		value: i,
		label: i === 0 ? '<1' : String(i),
	}))

	const updateRoom = (index: number, updatedRoom: Partial<Allocation>) => {
		const newRooms = rooms.map((room, i) =>
			i === index ? { ...room, ...updatedRoom } : room
		)
		setRooms(newRooms)
	}

	const handleAdultsChange = (index: number, count: number) => {
		if (count < MIN_ADULTS) return // Only block below min
		const currentRoom = rooms[index]
		const totalGuests = count + currentRoom.childAge.length
		if (totalGuests > MAX_COUNT_PER_ROOM && count > rooms[index].adults) return
		updateRoom(index, { adults: count })
	}

	const handleChildrenChange = (index: number, count: number) => {
		if (count < 0) return // Only block below min
		const currentRoom = rooms[index]
		const totalGuests = currentRoom.adults + count
		if (totalGuests > MAX_COUNT_PER_ROOM && count > currentRoom.childAge.length)
			return

		const currentChildCount = currentRoom.childAge.length
		let newChildAge = [...currentRoom.childAge]

		if (count > currentChildCount) {
			newChildAge = [
				...newChildAge,
				...Array(count - currentChildCount).fill(0),
			]
		} else if (count < currentChildCount) {
			newChildAge = newChildAge.slice(0, count)
		}

		updateRoom(index, { childAge: newChildAge })
	}

	const handleChildAgeChange = (
		roomIndex: number,
		childIndex: number,
		age: number
	) => {
		const newRooms = [...rooms]
		newRooms[roomIndex].childAge[childIndex] = age
		setRooms(newRooms)
	}

	const addRoom = () => {
		if (rooms.length < MAX_ROOMS) {
			setRooms([...rooms, { adults: 1, childAge: [] }])
		}

		emitAddRoomClickGAEvent()

		setTimeout(() => {
			scrollRef.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER })
		}, 100)
	}

	const removeRoom = (index: number) => {
		setRooms(rooms.filter((_, i) => i !== index))
	}

	const CustomMenuList = (props: MenuListProps<OptionType, false>) => {
		const { children } = props

		return (
			<CustomScroll rtl={isRtl}>
				<div className="max-h-[200px] rounded-t-none">{children}</div>
			</CustomScroll>
		)
	}

	const CustomSingleValue = (props: SingleValueProps<OptionType>) => {
		return (
			<components.SingleValue {...props}>
				<span
					className="text-base"
					data-test-id={`child-age-number-${props.data.label}`}
				>
					{props.data.label}
				</span>
			</components.SingleValue>
		)
	}

	const IndicatorsContainer = () => {
		return (
			<div className="flex items-center pr-3">
				<Icons.caret className="h-4 w-4 text-gray-500" />
			</div>
		)
	}

	const Disclaimer = ({ isDesktop }: { isDesktop?: boolean }) => {
		return (
			disclaimersArray.length > 0 && (
				<div
					className={classNames('guests-select__disclaimer', {
						[getDesktopBlockClass()]: isDesktop,
					})}
				>
					{disclaimersArray[0] === 'maxRooms'
						? t('roomsCountDisclaimer')
						: t('guestCountDisclaimer')}
				</div>
			)
		)
	}

	const handleSaveClick = () => {
		emitSaveGuestsClickGAEvent()
		if (isMobile && onSave) {
			// On mobile, onSave handles both applying changes and closing
			onSave(rooms)
		} else {
			// On desktop, apply changes and close immediately
			onSave && onSave(rooms)
			onClose()
		}
	}

	return (
		<>
			<div
				className={classNames('guests-select flex-1', {
					'view-mobile-layout': shouldViewMobileLayout,
				})}
			>
				<div ref={scrollRef} className="guests-select__rooms">
					{rooms.map((room, roomIndex) => {
						const shouldDisableIncrement =
							room.adults + room.childAge.length >= MAX_COUNT_PER_ROOM
						return (
							<div
								key={roomIndex}
								className="room-container"
								data-test-id="room-container"
							>
								<div
									className={`flex justify-between ${getMobileClass(
										'flex-col'
									)}`}
								>
									<div className="room-header">
										<div>
											{rooms.length > 1 && (
												<button
													onClick={() => removeRoom(roomIndex)}
													className={`${getDesktopClass(
														'me-3'
													)} flex items-center`}
												>
													<span
														className={`${getHiddenMobileClass()} text-score-good me-1 font-semibold text-sm`}
													>
														{t('remove')}
													</span>
													<Icons.iconClose
														className="w-5 h-5"
														stroke="#003E74"
													/>
												</button>
											)}
										</div>

										<h3 className="font-bold">
											{t('room')} {roomIndex + 1}
										</h3>
										<div
											className={`absolute ${getHiddenMobileClass()} w-[calc(100%+24px)] -left-3 -bottom-3 bg-text-300 h-[1px]`}
										/>
									</div>
									<div
										className={`grow room-content ${getMobileClass('mt-4')}`}
									>
										<div className="flex justify-between w-full">
											<CounterInput
												min={MIN_ADULTS}
												max={MAX_ADULTS_PER_ROOM}
												label={t('adults')}
												count={room.adults}
												onCountChange={(count) =>
													handleAdultsChange(roomIndex, count)
												}
												disableIncrement={shouldDisableIncrement}
											/>
											<CounterInput
												min={0}
												max={MAX_CHILDREN_PER_ROOM}
												label={t('children')}
												count={room.childAge.length}
												isChildInput
												onCountChange={(count) =>
													handleChildrenChange(roomIndex, count)
												}
												disableIncrement={shouldDisableIncrement}
											/>
										</div>
										{room.childAge.length > 0 && (
											<div className="child-ages-container">
												{room.childAge.map((age, childIndex) => (
													<div
														key={childIndex}
														className={classNames(
															'flex items-center justify-between space-x-2',
															{
																'mt-2': childIndex > 0,
															}
														)}
													>
														<span className="whitespace-nowrap">
															{t('ageOfChild')} {childIndex + 1}
															<span className="text-red-600"> *</span>
														</span>
														<Select<OptionType>
															isSearchable={false}
															className={`w-[88px] ${getDesktopClass(
																'w-26'
															)} rounded-lg`}
															classNames={{
																control: (props) => {
																	return `h-11 ![direction:ltr] !rounded-lg ${props.isFocused && props.menuIsOpen
																		? '!rounded-b-none !border-text-500'
																		: '!border-text-400'
																		} !shadow-none `
																},
																indicatorSeparator: () => 'hidden',
																menu: () => {
																	const closeToBottom =
																		isCloseToBottom(roomIndex)
																	const roundingClass = closeToBottom
																		? '!rounded-t-lg'
																		: '!rounded-b-lg'

																	return `z-[100] !border-text-500 !shadow-none border ${roundingClass}`
																},
																option: (props) => {
																	return `
																		px-3 py-2 border-b border-text-500 cursor-pointer last:border-b-0
																		${props.isSelected
																			? '!bg-text-100 !text-black transition-all'
																			: 'hover:bg-text-200 transition-colors'
																		}
																		${props.isFocused && !props.isSelected ? 'bg-text-100' : ''}
																	`
																},
															}}
															menuPlacement={
																isCloseToBottom(roomIndex) ? 'top' : 'bottom'
															}
															menuPortalTarget={
																typeof document !== 'undefined'
																	? document.body
																	: undefined
															}
															menuPosition="fixed"
															styles={{
																menuPortal: (provided) => {
																	return { ...provided, zIndex: 9999 } as any
																},
																menu: (provided) => {
																	return {
																		...provided,
																		marginTop: '-1px',
																		borderColor: '#E2E8F0',
																	} as any
																},
															}}
															options={NUMBER_OPTIONS_CHILDREN}
															value={NUMBER_OPTIONS_CHILDREN.find(
																(option) => option.value === age
															)}
															onChange={(option: SingleValue<OptionType>) => {
																if (option)
																	handleChildAgeChange(
																		roomIndex,
																		childIndex,
																		option.value
																	)
															}}
															placeholder={'Age'}
															components={{
																IndicatorsContainer: IndicatorsContainer as any,
																MenuList: CustomMenuList,
																SingleValue: CustomSingleValue,
															}}
														/>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
							</div>
						)
					})}
				</div>
				<div
					className={`${getHiddenDesktopClass()} ${getMobileClass(
						'px-0'
					)} p-4 flex justify-between`}
				>
					<Button
						onClick={addRoom}
						className={classNames(`btn-tertiary`, {
							'text-text-500 pointer-events-none': rooms.length >= MAX_ROOMS,
						})}
						name={t('addRoom')}
						id="add-room-button"
					>
						{rooms.length >= MAX_ROOMS ? (
							<Icons.lock className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" stroke="#94a3b8" />
						) : (
							<Icons.plus stroke="#003E74" className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
						)}
						{t('addRoom')}
					</Button>
					<Button
						onClick={handleSaveClick}
						id="save-rooms-and-guests-button"
						className="btn-secondary w-fit min-w-24"
						name={t('save')}
					>
						{t('save')}
					</Button>
				</div>
				<Disclaimer isDesktop />
			</div>
			<div
				className={`${getHiddenMobileClass()} guests-select__footer--mobile`}
			>
				<Disclaimer />
				<div className="flex-row justify-between p-4 flex">
					<Button
						onClick={addRoom}
						className={classNames(
							'!rounded-full text-left px-0 hover:shadow-none w-auto text-score-good justify-center font-bold flex items-center !text-base py-[0.625rem]',
							{ 'text-text-500 pointer-events-none': rooms.length >= MAX_ROOMS }
						)}
						name={t('addRoom')}
						id="add-room-button"
					>
						{rooms.length >= MAX_ROOMS ? (
							<Icons.lock className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" stroke="#94a3b8" />
						) : (
							<Icons.plus stroke="#003E74" className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
						)}
						{t('addRoom')}
					</Button>
					{isMainPage ? (
						<Button
							id="search-button"
							bgColor="main"
							textColor="white"
							onClick={onApplyGuestsAndSearch}
							name="Search"
							className="gradient-btn w-fit"
						>
							{t('newDesign.searchButton')}
						</Button>
					) : (
						<Button
							onClick={handleSaveClick}
							id="save-rooms-and-guests-button"
							className="btn-secondary w-fit min-w-24"
							name={t('save')}
						>
							{t('save')}
						</Button>
					)}
				</div>
			</div>
		</>
	)
}

export default GuestsSelect
